import { useEffect, useMemo, useState } from 'react';
import { useUser } from '@hooks';

/**
 * OP-3227: a Super Admin can run AI search and chat as a chosen school would see them.
 *
 *   me     the Super Admin's own access (Super Admins skip school exclusions)
 *   mine   as a teacher at the Super Admin's own school, with its exclusions applied
 *   other  as a teacher at a school picked from an account
 *
 * The page only ever sends the school's id. The server reads everything else about the school
 * itself, and refuses the id from anyone who is not a Super Admin.
 */
export type SimulateMode = 'me' | 'mine' | 'other';

export type SimulatedSchool = {
  id: string;
  name: string;
  /** The school's grade labels as stored, a JSON list. */
  grades?: string | null;
};

type SimulateState = {
  mode: SimulateMode;
  accountId?: string;
  school?: SimulatedSchool;
};

// Shared by both AI pages for the tab's lifetime, so moving between them keeps the choice.
const STORAGE_KEY = 'op3227.simulateSchool';
const DEFAULT_STATE: SimulateState = { mode: 'me' };

const read = (): SimulateState => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    return parsed && ['me', 'mine', 'other'].includes(parsed.mode) ? parsed : DEFAULT_STATE;
  } catch {
    return DEFAULT_STATE;
  }
};

const write = (state: SimulateState) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private windows and blocked storage just lose the choice on reload.
  }
};

export function useSimulateSchool() {
  const [user] = useUser();
  const [state, setState] = useState<SimulateState>(read);

  useEffect(() => write(state), [state]);

  const own = user?.schools?.[0];
  const target = useMemo<SimulatedSchool | undefined>(() => {
    if (state.mode === 'mine') return own?.id ? { id: own.id, name: own.name ?? 'My school', grades: own.grades } : undefined;
    if (state.mode === 'other') return state.school;
    return undefined;
  }, [state, own?.id, own?.name, own?.grades]);

  return {
    state,
    setState,
    /** The school the results are for, when simulating one. */
    target,
    /** What to send as asSchoolId: nothing when searching as oneself. */
    asSchoolId: target?.id,
    ownSchoolName: own?.name,
  };
}

export type SimulateSchoolState = ReturnType<typeof useSimulateSchool>;
